import { router } from "@inertiajs/react"
import { useState } from "react"

import {
  CUSTOM_INSTRUCTIONS_PLACEHOLDERS,
  CustomInstructionsField,
} from "@/components/agents/setup/custom-instructions"
import type {
  CalComEventType,
  CalendarKind,
  CalendlyEventType,
  ConnectedCalendars,
  SlackSetup,
  TransferPreset,
} from "@/components/agents/setup/types"
import type { DayHours } from "@/components/business-profile/hours-editor"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Tabs,
  TabsBar,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { agentPath } from "@/routes"

import type { PhoneOption } from "./form-specs"
import { DEFAULT_GREETING, readHours, readMessagesEnabled } from "./form-specs"
import { AppointmentsPanel } from "./panels/appointments-panel"
import { GeneralPanel } from "./panels/general-panel"
import { MessagesPanel } from "./panels/messages-panel"
import { TransfersPanel } from "./panels/transfers-panel"

// Only the slice of agent state the Edit sheet actually needs — anything
// beyond this is noise. Both SetupAgent (wizard pages) and DashboardAgent
// (dashboard page) are structurally assignable to this.
export interface EditableAgent {
  uid: string
  name: string
  setup_data: Record<string, unknown>
}

// Shape of the setup_data fields the Edit sheet owns. IMP-462: voice is NOT
// here — it's account-level identity (Settings → Identity), and the drawer
// must not resubmit a per-job copy.
export interface EditableFields {
  greeting: string
  custom_instructions: string
  phone_option: PhoneOption
  preferred_area_code: string
  forwarding_phone: string
  // IMP-492: the owner's control over taking messages. Delivery isn't part of
  // it — that's an account-level Notifications setting (IMP-375).
  messages_enabled: boolean
  appointments_enabled: boolean
  appointment_calendar_kind: CalendarKind | ""
  appointment_types: string
  appointment_booking_link: string
  appointment_event_type_uri: string
  appointment_calendar_id: string
  transfers_enabled: boolean
  handoff_presets: string[]
  handoff_custom_reasons: string[]
  handoff_phone: string
  // IMP-491: the weekly window transfers are allowed in. Empty means the
  // owner never set one, which the server reads as unrestricted.
  handoff_hours: DayHours[]
}

export function readFields(
  agent: EditableAgent,
  transferHoursPrefill: DayHours[],
): EditableFields {
  const d = agent.setup_data || {}
  const phoneOption: PhoneOption =
    d.phone_option === "forward" ? "forward" : "new"
  const calKind = d.appointment_calendar_kind
  const calendarKind: CalendarKind | "" =
    calKind === "cal_com" ||
    calKind === "calendly" ||
    calKind === "google_calendar" ||
    calKind === "outlook_calendar"
      ? calKind
      : ""
  const str = (k: string): string => {
    const v = d[k]
    return typeof v === "string" ? v : ""
  }
  const arr = (k: string): string[] =>
    Array.isArray(d[k])
      ? (d[k] as unknown[]).filter((x): x is string => typeof x === "string")
      : []
  return {
    greeting: str("greeting") || DEFAULT_GREETING,
    custom_instructions: str("custom_instructions"),
    phone_option: phoneOption,
    preferred_area_code: str("preferred_area_code"),
    forwarding_phone: str("forwarding_phone"),
    // IMP-492: reads the legacy message_channel for agents set up before
    // IMP-490, so an owner never has to reconfigure one. Same rule as the
    // server's Agents::Receptionist::MessageSettings.
    messages_enabled: readMessagesEnabled(d),
    appointments_enabled: d.appointments_enabled === true,
    appointment_calendar_kind: calendarKind,
    appointment_types: str("appointment_types"),
    appointment_booking_link: str("appointment_booking_link"),
    appointment_event_type_uri: str("appointment_event_type_uri"),
    appointment_calendar_id: str("appointment_calendar_id"),
    transfers_enabled: d.transfers_enabled === true,
    handoff_presets: arr("handoff_presets"),
    handoff_custom_reasons: arr("handoff_custom_reasons"),
    handoff_phone: str("handoff_phone"),
    handoff_hours: readHours(d, transferHoursPrefill),
  }
}

export function EditSheet({
  open,
  onOpenChange,
  agent,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
  transferPresets,
  transferHoursPrefill,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  agent: EditableAgent
  slack: SlackSetup
  connectedCalendars: ConnectedCalendars
  calendlyEventTypes: CalendlyEventType[]
  calComEventTypes: CalComEventType[]
  transferPresets: TransferPreset[]
  transferHoursPrefill: DayHours[]
}) {
  const [fields, setFields] = useState<EditableFields>(() =>
    readFields(agent, transferHoursPrefill),
  )
  const [processing, setProcessing] = useState(false)
  // IMP-168: the server rejects saving a Free-Calendly event type (no paid
  // plan). Surface that error on the Appointments panel and block Save until
  // the calendar/event type changes.
  const [eventTypeError, setEventTypeError] = useState<string | null>(null)
  const [wasOpen, setWasOpen] = useState(open)

  // Reset local state to the server's view every time the sheet opens so stale
  // edits from a previous open don't linger after the user cancels — adjusting
  // state during render (the React-recommended alternative to a setState
  // effect).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setFields(readFields(agent, transferHoursPrefill))
      setEventTypeError(null)
    }
  }

  function update<K extends keyof EditableFields>(
    key: K,
    value: EditableFields[K],
  ) {
    if (
      key === "appointment_calendar_kind" ||
      key === "appointment_event_type_uri"
    ) {
      setEventTypeError(null)
    }
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function save() {
    setProcessing(true)
    router.patch(
      agentPath({ id: agent.uid }),
      {
        setup_data: {
          ...fields,
          handoff_custom_reasons: fields.handoff_custom_reasons
            .map((r) => r.trim())
            .filter((r) => r.length > 0),
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
        onSuccess: () => onOpenChange(false),
        onError: (errors) =>
          setEventTypeError(
            (errors as Record<string, string>).appointment_event_type_uri ??
              null,
          ),
      },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="px-6 py-4">
          <SheetTitle>Edit {agent.name}</SheetTitle>
        </SheetHeader>

        <Tabs
          defaultValue="general"
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsBar>
            <TabsList variant="line">
              <TabsTrigger value="general">Greeting</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="appointments">Appointments</TabsTrigger>
              <TabsTrigger value="transfers">Transfers</TabsTrigger>
              <TabsTrigger value="customization">Customization</TabsTrigger>
            </TabsList>
          </TabsBar>

          <TabsContent
            value="general"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <GeneralPanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="messages"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <MessagesPanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="appointments"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <AppointmentsPanel
              fields={fields}
              onChange={update}
              connectedCalendars={connectedCalendars}
              calendlyEventTypes={calendlyEventTypes}
              calComEventTypes={calComEventTypes}
              agentUid={agent.uid}
              eventTypeError={eventTypeError}
            />
          </TabsContent>
          <TabsContent
            value="transfers"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <TransfersPanel
              fields={fields}
              onChange={update}
              transferPresets={transferPresets}
            />
          </TabsContent>
          <TabsContent
            value="customization"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <CustomInstructionsField
              value={fields.custom_instructions}
              placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS.receptionist}
              onChange={(value) => update("custom_instructions", value)}
            />
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t px-6 py-4">
          <Button
            className="w-full rounded-sm"
            onClick={save}
            disabled={processing || !!eventTypeError}
          >
            {processing ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
